import { useState } from "react";
import { publishBuyerFile } from "../api/client";
import type { PublishBuyerFileInput } from "../domain/publishedFiles";

type Props = {
  defaultPhone?: string;
  onPublished: (fileCode: string, buyerLink: string) => void;
};

export function OfficePublishForm({ defaultPhone = "", onPublished }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    buyerName: "",
    buyerLastName: "",
    buyerEmail: "",
    buyerPhoneE164: defaultPhone,
    propertyAddress: "",
    amountDue: "",
    purpose: "Cash to close"
  });

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const amountDue = Number.parseFloat(form.amountDue.replace(/[^0-9.]/g, ""));
      const payload: PublishBuyerFileInput = {
        buyerName: form.buyerName,
        buyerLastName: form.buyerLastName,
        buyerEmail: form.buyerEmail,
        buyerPhoneE164: form.buyerPhoneE164,
        propertyAddress: form.propertyAddress,
        amountDue,
        purpose: form.purpose,
        publishedBy: "escrow-officer"
      };
      const result = await publishBuyerFile(payload);
      onPublished(result.fileCode, result.buyerLink);
      setOpen(false);
      setForm({
        buyerName: "",
        buyerLastName: "",
        buyerEmail: "",
        buyerPhoneE164: defaultPhone,
        propertyAddress: "",
        amountDue: "",
        purpose: "Cash to close"
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not publish file");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="button-primary" onClick={() => setOpen(true)}>
        New payment request
      </button>
    );
  }

  return (
    <section className="panel mt-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold">Publish new buyer file</h2>
        <button type="button" className="button-secondary" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      <p className="text-sm text-[#596158]">
        Creates a new file code, office review record, and buyer link. Wire instructions use the office demo
        template until per-office templates ship.
      </p>
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={handleSubmit}>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Buyer legal name</span>
          <input
            className="w-full"
            required
            value={form.buyerName}
            onChange={(e) => setForm((f) => ({ ...f, buyerName: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Last name (lookup)</span>
          <input
            className="w-full"
            required
            value={form.buyerLastName}
            onChange={(e) => setForm((f) => ({ ...f, buyerLastName: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Buyer email</span>
          <input
            className="w-full"
            type="email"
            required
            value={form.buyerEmail}
            onChange={(e) => setForm((f) => ({ ...f, buyerEmail: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Buyer phone (E.164)</span>
          <input
            className="w-full"
            placeholder="+14155551234"
            required
            value={form.buyerPhoneE164}
            onChange={(e) => setForm((f) => ({ ...f, buyerPhoneE164: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm sm:col-span-2">
          <span className="field-label">Property address</span>
          <input
            className="w-full"
            required
            value={form.propertyAddress}
            onChange={(e) => setForm((f) => ({ ...f, propertyAddress: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Amount due (USD)</span>
          <input
            className="w-full"
            inputMode="decimal"
            required
            placeholder="184217.42"
            value={form.amountDue}
            onChange={(e) => setForm((f) => ({ ...f, amountDue: e.target.value }))}
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="field-label">Purpose</span>
          <input
            className="w-full"
            value={form.purpose}
            onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
          />
        </label>
        {error ? <p className="text-sm text-red-700 sm:col-span-2">{error}</p> : null}
        <div className="sm:col-span-2">
          <button type="submit" className="button-primary" disabled={busy}>
            {busy ? "Publishing…" : "Publish file & open dashboard"}
          </button>
        </div>
      </form>
    </section>
  );
}
